'use client';

/**
 * Public lobby/root intro: the single visible <h1> + supporting copy, rendered above the
 * game grid so crawlers get a clear page heading (the grids only have <h2>s).
 *
 * Rendered ONLY for signed-out visitors (gated by the caller). A compact dark hero that
 * echoes the Skillz Arcade brand (app icon + blue→purple→pink gradient, matching the OG
 * image / app icon).
 *
 * Client component so it can carry a dismiss "×" (persisted in localStorage) — but the
 * <h1> still server-renders (Next SSRs client components), so crawlers/screen-readers get
 * the full heading regardless of JS. The gradient on "Skillz Arcade" is purely visual
 * (bg-clip-text), so the heading text stays intact.
 */

import {useEffect, useState} from 'react';
import Image from 'next/image';
import arcadeIcon from '@/../public/icons/skillz-arcade-icon-192.png';

// Brand gradient (primary-blue → violet → primary-red) — same identity as the app icon/OG.
const BRAND_GRADIENT = 'linear-gradient(90deg, #2f7bff 0%, #8b5cf6 50%, #EA0449 100%)';
const DISMISS_KEY = 'skillz_arcade_intro_dismissed';

// Default outer wrapper = the narrow WebArcadeLobby column. The root-multicade surface
// renders it inside ArcadeShell's <main> (already padded, full width), so it passes a
// bare `className` (e.g. 'mb-6') to fill that column instead.
const LOBBY_WRAPPER =
  'mx-auto w-full max-w-[430px] px-4 pt-4 sm:max-w-[520px] sm:px-5 sm:pt-6 md:max-w-[600px] md:px-6';

const ArcadeIntro = ({className}: {className?: string}) => {
  const [dismissed, setDismissed] = useState(false);

  // SSR renders it (dismissed=false) so the <h1> is always in the initial HTML for SEO;
  // on the client we hide it if the visitor dismissed it before.
  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch {
      /* private mode / storage disabled — just keep showing it */
    }
  }, []);

  if (dismissed) return null;

  const close = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <section className={className ?? LOBBY_WRAPPER}>
      <div
        className='relative rounded-2xl border border-white/10 p-3.5 shadow-lg shadow-black/20 sm:p-4'
        style={{
          backgroundImage:
            'radial-gradient(120% 140% at 0% 0%, #241d3d 0%, #17141f 58%, #100e16 100%)',
        }}
      >
        <button
          type='button'
          onClick={close}
          aria-label='Dismiss'
          className='absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-white/45 transition-colors hover:bg-white/10 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30'
        >
          <svg width='13' height='13' viewBox='0 0 14 14' fill='none' aria-hidden>
            <path
              d='M1 1l12 12M13 1L1 13'
              stroke='currentColor'
              strokeWidth='1.8'
              strokeLinecap='round'
            />
          </svg>
        </button>
        <div className='flex items-center gap-2.5 pr-7'>
          <Image
            src={arcadeIcon}
            alt=''
            aria-hidden
            width={38}
            height={38}
            priority
            className='h-[38px] w-[38px] shrink-0 rounded-[10px]'
          />
          <h1 className='text-[18px] font-extrabold leading-[22px] text-white sm:text-[21px] sm:leading-[25px]'>
            Play Online Games in the{' '}
            <span
              className='bg-clip-text text-transparent'
              style={{backgroundImage: BRAND_GRADIENT}}
            >
              Skillz Arcade
            </span>
          </h1>
        </div>
        <p className='mt-2.5 text-[12.5px] leading-[17px] text-white/70 sm:text-[13.5px] sm:leading-[19px]'>
          Play Skillz games instantly in your browser. Choose from bingo, card, puzzle, tile,
          dice, and arcade games, compete against real players, and discover live events and
          tournaments.
        </p>
      </div>
    </section>
  );
};

export default ArcadeIntro;
